import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shield, ShieldCheck, ShieldOff, Trash2, Plus, Webhook } from "lucide-react";

const MILESTONES = ["BOOKED","PICKED_UP","IN_TRANSIT","AT_HUB","CUSTOMS","OUT_FOR_DELIVERY","DELIVERED","EXCEPTION"];
const CHANNELS = ["email", "sms", "webhook"];

export function NotificationPrefs() {
  const [prefs, setPrefs] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [newHook, setNewHook] = useState("");
  const [busy, setBusy] = useState(false);

  const loadPrefs = () => api.get("/v1/me/notification-prefs").then(({data})=>setPrefs(data.prefs));
  const loadHooks = () => api.get("/v1/me/webhooks").then(({data})=>setWebhooks(data.items || []));

  useEffect(() => { loadPrefs(); loadHooks(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/v1/me/notification-prefs", prefs);
      toast.success("Preferences saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addHook = async () => {
    if (!newHook) return;
    try {
      await api.post("/v1/me/webhooks", { url: newHook, event_types: ["*"], active: true });
      setNewHook(""); loadHooks(); toast.success("Webhook added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delHook = async (id) => {
    try { await api.delete(`/v1/me/webhooks/${id}`); loadHooks(); toast.success("Webhook removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!prefs) return <PortalLayout section="customer"><div className="vtx-skeleton h-24" /></PortalLayout>;

  return (
    <PortalLayout section="customer">
      <div className="vtx-eyebrow">/ Notifications</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Notification preferences</h1>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="vtx-card p-6">
          <div className="vtx-eyebrow">Channels</div>
          <div className="mt-4 space-y-3">
            {CHANNELS.map(ch => (
              <label key={ch} className="flex items-center justify-between p-3 border border-[#A9C0E0]/50" style={{borderRadius:2}}>
                <div>
                  <div className="font-semibold text-[#0E2F76] capitalize">{ch}</div>
                  <div className="text-xs text-[#475569]">
                    {ch === "email" && "Milestone updates via email (pluggable: SendGrid/Resend/SES)"}
                    {ch === "sms" && "Text messages (pluggable: Twilio, M-Pesa SMS)"}
                    {ch === "webhook" && "Send JSON payloads to your configured endpoints"}
                  </div>
                </div>
                <input type="checkbox" checked={!!prefs.channels?.[ch]} onChange={(e)=>setPrefs({...prefs, channels: {...(prefs.channels||{}), [ch]: e.target.checked}})} data-testid={`ch-${ch}`} />
              </label>
            ))}
          </div>
        </div>
        <div className="vtx-card p-6">
          <div className="vtx-eyebrow">Milestones</div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {MILESTONES.map(m => (
              <label key={m} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={prefs.milestones?.[m] !== false} onChange={(e)=>setPrefs({...prefs, milestones: {...(prefs.milestones||{}), [m]: e.target.checked}})} data-testid={`ms-${m}`} />
                <span className="text-[#0f172a] font-medium">{m.replace(/_/g," ")}</span>
              </label>
            ))}
          </div>
          <button className="vtx-btn-primary mt-6" onClick={save} disabled={busy} data-testid="save-prefs">Save preferences</button>
        </div>
      </div>

      <div className="mt-6 vtx-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="vtx-eyebrow inline-flex items-center gap-2"><Webhook size={12}/> Webhooks</div>
        </div>
        <div className="flex gap-3">
          <input className="vtx-input flex-1" placeholder="https://your-erp.example/hooks/ventrix" value={newHook} onChange={(e)=>setNewHook(e.target.value)} data-testid="webhook-url" />
          <button className="vtx-btn-primary inline-flex items-center gap-2" onClick={addHook} data-testid="add-webhook"><Plus size={14}/> Add</button>
        </div>
        <div className="mt-4 space-y-2">
          {webhooks.length === 0 && <div className="text-sm text-[#475569]">No webhooks yet.</div>}
          {webhooks.map(w => (
            <div key={w.id} className="flex items-center justify-between p-3 border border-[#A9C0E0]/40" style={{borderRadius:2}}>
              <div>
                <div className="font-mono text-sm text-[#0E2F76]">{w.url}</div>
                <div className="text-xs text-[#475569]">Events: {(w.event_types || ["*"]).join(", ")}</div>
              </div>
              <button className="text-[#d41f26] hover:text-[#b3161c]" onClick={()=>delHook(w.id)} data-testid={`del-webhook-${w.id}`}><Trash2 size={16}/></button>
            </div>
          ))}
        </div>
      </div>
    </PortalLayout>
  );
}

export function Security() {
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetup(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/enable", { code });
      setSetup(null); setCode(""); await refresh(); toast.success("MFA enabled");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code });
      setCode(""); await refresh(); toast.success("MFA disabled");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <PortalLayout section={user?.role !== "customer" ? "ops" : "customer"}>
      <div className="vtx-eyebrow">/ Security</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Account security</h1>

      <div className="mt-6 max-w-2xl">
        <div className="vtx-card p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="vtx-eyebrow inline-flex items-center gap-2"><Shield size={12}/> Multi-factor authentication</div>
              <div className="mt-2 text-sm text-[#475569]">Time-based one-time password (TOTP). Compatible with Google Authenticator, Authy, 1Password.</div>
            </div>
            {user?.mfa_enabled ? <span className="vtx-chip" style={{background:"#DCFCE7", color:"#166534"}}><ShieldCheck size={12}/> Enabled</span>
                                 : <span className="vtx-chip" style={{background:"#FEF3C7", color:"#B45309"}}><ShieldOff size={12}/> Disabled</span>}
          </div>

          {!user?.mfa_enabled && !setup && (
            <button className="vtx-btn-primary mt-6" onClick={start} disabled={busy} data-testid="mfa-setup">Set up MFA</button>
          )}
          {setup && (
            <div className="mt-6">
              <div className="text-sm text-[#0f172a]">Scan this QR with your authenticator, then enter the 6-digit code:</div>
              <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="TOTP QR" className="mt-3 border border-[#A9C0E0]/60 w-40 h-40" />
              <div className="mt-2 text-[10px] font-mono text-[#475569] break-all">Manual key: {setup.secret}</div>
              <div className="mt-4 flex gap-2">
                <input className="vtx-input font-mono tracking-widest text-center max-w-xs" placeholder="123456" value={code} onChange={(e)=>setCode(e.target.value)} data-testid="mfa-code" />
                <button className="vtx-btn-primary" onClick={enable} disabled={busy} data-testid="mfa-enable">Enable MFA</button>
              </div>
            </div>
          )}
          {user?.mfa_enabled && (
            <div className="mt-6 flex gap-2">
              <input className="vtx-input font-mono tracking-widest text-center max-w-xs" placeholder="Current 6-digit code" value={code} onChange={(e)=>setCode(e.target.value)} data-testid="mfa-disable-code" />
              <button className="vtx-btn-danger" onClick={disable} disabled={busy} data-testid="mfa-disable">Disable MFA</button>
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
